import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import { useAccountState } from 'contexts/account/account-context';

import Breadcrumbs from 'components/breadcrumbs';
import Autocomplete from 'components/autocomplete';
import { AccountCompany } from 'contexts/account/types/account-company.type';
import { useGet } from 'hooks/use-get';
import { useIntl } from 'react-intl';
import { UserType } from 'types/shared/user-type.enum';
import { createFuzzyFilterOptions } from 'utils/fuzzy-filter';
import { groupBy } from 'underscore';
import googleTagManager from 'utils/google-tag-manager';
import { saveState } from 'utils/storage';
import { getMarketContacts } from 'views/settings/api/market-contacts';
import ChangeLanguage from 'views/settings/components/change-language';
import ChangeLocale from 'views/settings/components/change-locale';
import { Contact, ContactType } from 'views/settings/types/contact.type';
import SettingsTabHeader from './components/settings-tabs-header';
import { Box, Divider, Grid, Typography } from '@mui/material';

type SettingsProps = {};

const Settings: React.FC<SettingsProps> = () => {
  const history = useHistory();
  const intl = useIntl();
  const { account, setAccount } = useAccountState();

  // STATE
  const [contactsData, setContactsData] = useState<Record<ContactType, Contact[]>>({
    [ContactType.cargillContact]: [],
    [ContactType.marketContact]: [],
  });

  // #region FETCH DATA

  const { data } = useGet(['getContacts', account.company?.marketId], ({ queryKey }) => getMarketContacts(Number(queryKey[1])));

  //#endregion FETCH DATA

  const selectedCompany = account.companies.find((company) => company.companyId === account.company?.companyId) ?? null;

  const setCompany = (company: AccountCompany | null) => {
    if (company) {
      saveState({ key: 'companyId', state: company.companyId });

      setAccount(company.companyId);

      googleTagManager.push({
        event: 'select_company',
        companyId: company.companyId,
        companyType: company.companyType,
        marketId: company.marketId,
        marketName: company.marketName,
        marketIndustry: company.aquaSpecies,
        userType: (account.currentUser?.userType as UserType) || '',
        pageUrl: window.location.pathname,
      });

      history.push('/');
    }
  };

  useEffect(() => {
    const groupedContactsData = groupBy(data!!, 'contactType') as Record<ContactType, Contact[]>;
    setContactsData(groupedContactsData);
  }, [data]);

  return (
    <>
      <SettingsTabHeader utils={<Breadcrumbs />} />

      <Grid container spacing={4} columnSpacing={20}>
        <Grid size={{ xs: 12, md: 6 }} maxHeight={241} display={'grid'} alignItems={'center'} gap={4}>
          <Autocomplete
            value={selectedCompany}
            disabled={account.companies.length <= 1}
            onChange={(_, company) => setCompany(company)}
            fullWidth
            label={intl.formatMessage({ id: 'general.company' })}
            noOptionsText={intl.formatMessage({ id: 'general.noResults' })}
            getOptionLabel={(company) => company.customerName}
            isOptionEqualToValue={(option, company) => option.companyId === company.companyId}
            filterOptions={createFuzzyFilterOptions((company) => `${company.customerErpId} ${company.customerName}`)}
            options={account.companies || []}
          />

          <ChangeLanguage fullWidth />
          <ChangeLocale fullWidth />

          {/* <ToggleSnowfall /> */}
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} color="white">
          <Typography color={'white'}>{intl.formatMessage({ id: 'settings.needHelp' })}</Typography>
          <Typography color={'white'} mt={4} mb={4}>
            {intl.formatMessage({ id: 'settings.marketContactsText' })}
          </Typography>
          {!!contactsData?.marketContact?.length && (
            <>
              <Divider color="white" />
              <Typography mt={4}>{intl.formatMessage({ id: 'settings.marketContacts' })}:</Typography>
              {contactsData?.marketContact.map((marketContact) => (
                <Box mt={4} key={marketContact.id}>
                  <Typography>
                    {intl.formatMessage({ id: 'settings.name' })}: {`${marketContact.name}`}
                  </Typography>
                  <Typography>
                    {intl.formatMessage({ id: 'settings.email' })}: {marketContact.email}
                  </Typography>
                  <Typography>
                    {intl.formatMessage({ id: 'settings.phone' })}: {marketContact.phone}
                  </Typography>
                </Box>
              ))}
            </>
          )}
          {!!contactsData?.cargillContact?.length && (
            <>
              <Divider color="white" sx={{ marginTop: 4 }} />
              <Typography color={'white'} my={4}>
                {intl.formatMessage({ id: 'settings.cargillContact' })}:
              </Typography>
              {contactsData?.cargillContact.map((cargillContact) => (
                <Box key={cargillContact.id} display={'flex'} flexDirection={'column'} gap={1}>
                  <Typography>
                    {intl.formatMessage({ id: 'settings.name' })}: {cargillContact.name}
                  </Typography>
                  <Typography>
                    {intl.formatMessage({ id: 'settings.email' })}: {cargillContact.email}
                  </Typography>
                  <Typography>
                    {intl.formatMessage({ id: 'settings.phone' })}: {cargillContact.phone}
                  </Typography>
                </Box>
              ))}
            </>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default Settings;
